import {call, put, takeLatest} from 'redux-saga/effects';
import Swal from "sweetalert2";
import {actions as usersActions, types as UsersTypes} from '../Ducks/users';

import {callApi} from './utils';
import * as api from '../API/users';


function* getUsersList(action) {
    const {success, failure} = usersActions.saga.getUsersList;

    yield callApi(
        call(api.getUsersList, action.data),
        success,
        failure
    )
}

function* getUserDetails(action) {
    const {success, failure} = usersActions.saga.getUserDetails;

    yield callApi(
        call(api.getUserDetails, action.data),
        success,
        failure
    )
}

function* getTeams(action) {
    const {success, failure} = usersActions.saga.getTeams;

    yield callApi(
        call(api.getTeams, action.data),
        success,
        failure
    )
}

function* addTeam(action) {
    const {success, failure} = usersActions.saga.addTeam;

    function* postApiSuccessCallEffect(_res) {
        yield call(getTeams, action);
    }

    yield callApi(
        call(api.addTeam, action.data),
        success,
        failure,
        {postApiSuccessCallEffect}
    )
}

function* deleteTeam(action) {
    const {success, failure} = usersActions.saga.deleteTeam;

    function* postApiSuccessCallEffect(_res) {
        if (action.data.setTeamID) {
            action.data.setTeamID(null);
        }
        // Preserve the "Show deleted" toggle state when refreshing the list.
        yield call(getTeams, {data: {showDeleted: action.data.showDeleted}});
    }

    yield callApi(
        call(api.deleteTeam, action.data),
        success,
        failure,
        {postApiSuccessCallEffect}
    )
}

function* restoreTeam(action) {
    const {success, failure} = usersActions.saga.restoreTeam;

    function* postApiSuccessCallEffect(_res) {
        // Keep the deleted teams visible after a restore so the user sees the result.
        yield call(getTeams, {data: {showDeleted: action.data.showDeleted}});
    }

    yield callApi(
        call(api.restoreTeam, action.data),
        success,
        failure,
        {postApiSuccessCallEffect}
    )
}

function* updateTeam(action) {
    const {success, failure} = usersActions.saga.updateTeam;

    function* postApiSuccessCallEffect(_res) {
        yield call(getTeams, action);
    }

    yield callApi(
        call(api.updateTeam, action.data),
        success,
        failure,
        {postApiSuccessCallEffect}
    )
}

function* updateUser(action) {
    const {success, failure} = usersActions.saga.updateUser;

    function* postApiSuccessCallEffect(res) {
        // The backend returns a non-2xx status for every real error (e.g. 422
        // with {error_code, error} — confirmed via getResponse() in helpers.php,
        // which discards any 'error_message' key it was given and always writes
        // the response body out as 'error'), which rejects the fetch promise and
        // is handled by `failure` before this callback ever runs. This checks
        // the actual field name the API writes (not the old `res.error[1]`,
        // which no real response shape has — that crashed here on every save,
        // silently blocking this refresh) so a genuine error is still shown
        // correctly if one were ever to reach this point.
        if (res && res.error) {
            Swal.fire({
                icon: 'error',
                title: res.error
            })
        } else {
            Swal.fire({
                icon: 'success',
                title: 'Saved successfully!',
                showConfirmButton: false,
                timer: 1500
            })

            // A team change is logged on the backend; refresh the team history
            // and profile so the "Team since" field and history list reflect it.
            if (action.data.key === 'team_id') {
                yield call(getUserTeamHistory, {data: action.data.id});
                yield call(getUserDetails, {data: action.data.id});
            }
        }
    }

    function* postApiFailureCallEffect(error) {
        Swal.fire({
            icon: 'error',
            title: (error && error.message) || 'Something went wrong'
        })

        // A team switch can fail because the team was soft-deleted in another tab
        // after this page's team dropdown was loaded (now stale). Refresh the teams
        // list so the deleted team disappears, and re-fetch the profile so the Team
        // field reverts to the user's previously saved team.
        if (action.data.key === 'team_id') {
            yield call(getTeams, {data: {showDeleted: false}});
            yield call(getUserDetails, {data: action.data.id});
        }
    }

    yield callApi(
        call(api.updateUser, action.data),
        success,
        failure,
        {postApiSuccessCallEffect, postApiFailureCallEffect}
    )
}

function* getUserTeamHistory(action) {
    const {success, failure} = usersActions.saga.getUserTeamHistory;

    yield callApi(
        call(api.getUserTeamHistory, action.data),
        success,
        failure
    )
}

function* addTeamTransition(action) {
    const {success, failure} = usersActions.saga.addTeamTransition;

    try {
        const res = yield call(api.addTeamTransition, action.data);
        yield put(success(res));

        Swal.fire({
            icon: 'success',
            title: 'Team transition added',
            showConfirmButton: false,
            timer: 1500
        });

        yield call(getUserTeamHistory, {data: action.data.id});
        yield call(getUserDetails, {data: action.data.id});
    } catch (error) {
        yield put(failure(error));

        Swal.fire({
            icon: 'error',
            title: (error && error.message) || 'Something went wrong'
        });

        // The chosen team may have been soft-deleted in another tab after this
        // page's team dropdown was loaded. Refresh so the deleted team no longer
        // appears the next time the Add Transition dropdown is opened.
        yield call(getTeams, {data: {showDeleted: false}});
    }
}

function* editTeamTransition(action) {
    const {success, failure} = usersActions.saga.editTeamTransition;

    try {
        const res = yield call(api.editTeamTransition, action.data);
        yield put(success(res));

        Swal.fire({
            icon: 'success',
            title: 'Team transition updated',
            showConfirmButton: false,
            timer: 1500
        });

        yield call(getUserTeamHistory, {data: action.data.id});
        yield call(getUserDetails, {data: action.data.id});
    } catch (error) {
        yield put(failure(error));

        Swal.fire({
            icon: 'error',
            title: (error && error.message) || 'Something went wrong'
        });
    }
}

function* createUser(action) {
    const {success, failure} = usersActions.saga.createUser;

    function* postApiSuccessCallEffect(res) {
        if (res.error) {
            Swal.fire({
                icon: 'error',
                title: res.message,
            })
        } else {
            Swal.fire({
                icon: res.error ? 'warning' : 'success',
                title: res.message,
                showConfirmButton: false,
                timer: 1500
            }).then(() => {
                action.data.navigateTo();
            })
        }
    }

    yield callApi(
        call(api.createUser, action.data),
        success,
        failure,
        {postApiSuccessCallEffect}
    )
}

function* getCaseTeams(action) {
    const {success, failure} = usersActions.saga.getCaseTeams;
    yield callApi(
        call(api.getCaseTeams, action.data),
        success,
        failure,
    )
}

function* getTeamDetails(action) {
    const {success, failure} = usersActions.saga.getTeamDetails;

    yield callApi(
        call(api.getTeamDetails, action.data),
        success,
        failure,
    )
}

function* getActiveTeamUsers(action) {
    const {success, failure} = usersActions.saga.getActiveTeamUsers;

    yield callApi(
        call(api.getActiveTeamUsers, action.data),
        success,
        failure,
    )
}

function* getActiveUsers(action) {
    const {success, failure} = usersActions.saga.getActiveUsers;

    yield callApi(
        call(api.getActiveUsers, action.data),
        success,
        failure,
    )
}


export default function* rootCustomerSaga() {
    yield takeLatest(UsersTypes.getUsersList, getUsersList);
    yield takeLatest(UsersTypes.getUserDetails, getUserDetails);
    yield takeLatest(UsersTypes.getTeams, getTeams);
    yield takeLatest(UsersTypes.addTeam, addTeam);
    yield takeLatest(UsersTypes.updateTeam, updateTeam);
    yield takeLatest(UsersTypes.deleteTeam, deleteTeam);
    yield takeLatest(UsersTypes.restoreTeam, restoreTeam);
    yield takeLatest(UsersTypes.updateUser, updateUser);
    yield takeLatest(UsersTypes.getUserTeamHistory, getUserTeamHistory);
    yield takeLatest(UsersTypes.addTeamTransition, addTeamTransition);
    yield takeLatest(UsersTypes.editTeamTransition, editTeamTransition);
    yield takeLatest(UsersTypes.createUser, createUser);
    yield takeLatest(UsersTypes.getCaseTeams, getCaseTeams);
    yield takeLatest(UsersTypes.getTeamDetails, getTeamDetails);
    yield takeLatest(UsersTypes.getActiveTeamUsers, getActiveTeamUsers);
    yield takeLatest(UsersTypes.getActiveUsers, getActiveUsers);
}